import clsx from 'clsx'
import _ from 'lodash'
import { Fragment, ReactNode, useEffect, useState } from 'react'

const ROW_STYLE = 'flex w-full pl-5 px-4 py-2 border-b last:border-0 cursor-pointer'

type PanelProps<T, G extends string> = {
  title: string
  getItems: () => Promise<T[]>
  getId: (item: T) => string
  getGroup: (item: T) => G
  getGroupColor: (group: G) => string
  getRow: (item: T) => ReactNode
  getDetails: (item: T) => ReactNode
  defaultGroup?: G
}

export function Panel<T, G extends string>({
  title,
  getItems,
  getGroup,
  getGroupColor,
  defaultGroup,
  ...itemProps
}: PanelProps<T, G>) {
  const [{ items, status }, setData] = useState({
    items: [] as T[],
    status: 'loading' as 'loading' | 'error' | 'success',
  })
  const [activeId, setActiveId] = useState('')

  useEffect(() => {
    getItems()
      .then((items) => setData({ items, status: 'success' }))
      .catch((err: unknown) => {
        setData((prev) => ({ ...prev, status: 'error' }))
        console.error(err)
      })
  }, [getItems])

  const itemsByGroup = _.groupBy(items, getGroup)
  return (
    <div
      className={clsx(
        'border',
        'rounded overflow-hidden',
        'shadow-sm',
        'flex items-start flex-col',
        'border-b',
        'bg-neutral-100',
        'mt-5',
      )}
    >
      <div className="flex items-center text-lg font-mono border-b w-full pl-5 p-4">
        <span className="flex-grow">
          {title} ({status === 'success' ? incidents(items.length) : ''})
        </span>
      </div>
      {status === 'loading' && <div className="p-5">Loading...</div>}
      {status === 'error' && <div className="p-5">Error</div>}
      {status === 'success' &&
        Object.entries(itemsByGroup).map(([group, items], idx) => (
          <PanelGroup
            key={group}
            color={getGroupColor(group as G)}
            group={group}
            isDefaultGroup={defaultGroup?.length ? defaultGroup === group : idx === 0}
            items={items}
            activeId={activeId}
            setActiveId={setActiveId}
            {...itemProps}
          />
        ))}
    </div>
  )
}

type ItemProps<T, G extends string> = Omit<PanelProps<T, G>, 'title' | 'getItems' | 'getGroup' | 'getGroupColor'> & {
  activeId?: string
  setActiveId: (id: string) => void
}

function PanelGroup<T, G extends string>({
  group,
  color,
  items,
  activeId,
  setActiveId,
  getId,
  getRow,
  getDetails,
  isDefaultGroup,
}: ItemProps<T, G> & { group: string; color: string; items: T[]; isDefaultGroup: boolean }) {
  const [expanded, setExpanded] = useState(isDefaultGroup)
  return (
    <>
      <div className={clsx(ROW_STYLE, expanded && 'bg-lime-50')} onClick={() => setExpanded(!expanded)}>
        <span className={clsx('w-[100px] flex items-center', color)}>{_.capitalize(group)}</span>
        <span className="w-[300px]">({incidents(items.length)})</span>
      </div>
      {expanded &&
        items.map((item) => {
          const id = getId(item)
          const isActive = id === activeId
          return (
            <Fragment key={id}>
              <div
                className={clsx(ROW_STYLE, isActive && 'bg-lime-50')}
                onClick={() => setActiveId(!isActive ? id : '')}
              >
                {getRow(item)}
              </div>
              {isActive && getDetails(item)}
            </Fragment>
          )
        })}
    </>
  )
}

function incidents(n: number) {
  return `${n} incident${n == 1 ? '' : 's'}`
}
